import { useRef, useState } from "react";
import { FileText, Upload, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DemographicsFields } from "@/pages/careers/DemographicsFields";
import { cn } from "@/lib/utils";

export const FIELDS = [["name", "Full name", "text", true, "name"], ["email", "Email", "email", true, "email"], ["phone", "Phone", "tel", false, "tel"],
  ["location", "Location", "text", false, "address-level2"], ["linkedin", "LinkedIn profile", "url", false, "url"]];

export function StepAbout({ f, setF, errors }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {FIELDS.map(([k, label, type, req, ac]) => (
        <div key={k} className={cn("space-y-1.5", (k === "linkedin" || k === "name") && "sm:col-span-2")}>
          <Label htmlFor={`apply-${k}`}>{label}{!req && <span className="text-ink-muted"> (optional)</span>}</Label>
          <Input id={`apply-${k}`} type={type} autoComplete={ac} value={f[k]} aria-invalid={!!errors[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
            className={cn("h-11 text-base", errors[k] && "border-risk")} data-testid={`apply-${k}-input`} />
          {errors[k] && <p className="text-xs text-risk" data-testid={`apply-${k}-error`}>{errors[k]}</p>}
        </div>
      ))}
    </div>
  );
}

export function StepResume({ file, setFile, color, error }) {
  const [over, setOver] = useState(false);
  const input = useRef(null);
  const onDrop = (e) => { e.preventDefault(); setOver(false); const fl = e.dataTransfer.files?.[0]; if (fl) setFile(fl); };
  return (
    <div className="space-y-2">
      <label onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop}
        className={cn("flex min-h-[180px] cursor-pointer flex-col items-center justify-center gap-3 rounded-[6px] border-2 border-dashed px-4 py-8 text-center transition-colors duration-150",
          over ? "border-brand bg-brand-tint" : error ? "border-risk bg-risk-tint/40" : "border-line bg-subtle hover:bg-white")} data-tour="pt-apply-resume-dropzone" data-testid="apply-resume-dropzone">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-card"><Upload size={20} style={{ color }} aria-hidden="true" /></span>
        <span className="text-sm font-medium text-ink">Drag your resume here, or tap to choose a file</span>
        <span className="text-xs text-ink-muted">PDF, DOCX or DOC · max 10 MB</span>
        <input ref={input} type="file" accept=".pdf,.docx,.doc" className="sr-only" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="apply-resume-input" />
      </label>
      {file && (
        <div className="flex items-center gap-3 rounded-[5px] border border-line bg-white px-3 py-2 text-sm" data-testid="apply-resume-file">
          <FileText size={16} className="text-brand" aria-hidden="true" /><span className="flex-1 truncate">{file.name}</span>
          <span className="text-xs text-ink-muted">{(file.size / 1024).toFixed(0)} KB</span>
          <button type="button" onClick={() => { setFile(null); if (input.current) input.current.value = ""; }} aria-label="Remove file" className="text-ink-muted hover:text-risk"><X size={16} /></button>
        </div>
      )}
      {error && <p className="text-xs text-risk" data-testid="apply-resume-error">{error}</p>}
    </div>
  );
}

function YesNo({ value, onChange, testId, label }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex w-full rounded-[5px] border border-line bg-subtle p-1 sm:w-auto" data-testid={testId}>
      {["yes", "no"].map((v) => (
        <button type="button" role="radio" aria-checked={value === v} key={v} onClick={() => onChange(v)} data-testid={`${testId}-${v}`}
          className={cn("h-10 flex-1 rounded-[4px] px-6 text-sm font-medium capitalize transition-[background-color,color,box-shadow] duration-150",
            value === v ? "bg-white text-brand shadow-card" : "text-ink-secondary hover:text-ink")}>{v}</button>
      ))}
    </div>
  );
}

export function StepQuestions({ job, ko, setKo, custom, setCustom, demo, setDemo, errors }) {
  return (
    <div className="space-y-6">
      {job.knockout_questions.map((q, i) => (
        <div key={q} className="space-y-2"><p className="text-sm font-medium text-ink">{q}</p>
          <YesNo value={ko[q]} onChange={(v) => setKo({ ...ko, [q]: v })} testId={`apply-knockout-${i}`} label={q} />
          {errors[`ko-${i}`] && <p className="text-xs text-risk">{errors[`ko-${i}`]}</p>}</div>
      ))}
      {job.custom_questions.map((q) => (
        <div key={q.id} className="space-y-1.5">
          <Label htmlFor={`apply-custom-${q.id}`}>{q.question}{!q.required && <span className="text-ink-muted"> (optional)</span>}</Label>
          <Textarea id={`apply-custom-${q.id}`} value={custom[q.id] || ""} onChange={(e) => setCustom({ ...custom, [q.id]: e.target.value })} className="min-h-[96px] text-base" data-testid={`apply-custom-${q.id}`} />
          {errors[`c-${q.id}`] && <p className="text-xs text-risk">{errors[`c-${q.id}`]}</p>}
        </div>
      ))}
      {!job.knockout_questions.length && !job.custom_questions.length && <p className="text-sm text-ink-secondary">No role-specific questions for this job.</p>}
      <DemographicsFields value={demo} onChange={setDemo} />
    </div>
  );
}
